import {
  GetConsentStatusResponseBodyPurposesItemDTO,
  GetConsentStatusResponseBodySubscriptionsItemDTO,
  GetPermitTransactionV2ResponseBodyDTO,
  GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO,
  PermitHistoryDTO,
} from '@ketch-com/figurehead'
import { useSetSubscriptions } from 'api/externalApis/ketchCdn/mutations/useSetSubscriptions'
import { useSetConsent } from 'api/externalApis/ketchCdn/mutations/useSetConsent'
import { auditLogTabs } from 'pages/insights/auditLog/AuditLog'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCurrentOrganization, useQueryParams } from 'utils/hooks'
import { createCustomContext } from 'utils/hooks/createCustomContext'
import {
  ConsentSource,
  PermitRightContext,
  SetConsentRequest,
  SetConsentResponse,
  SetSubscriptionsRequest,
  SubscriptionSource,
  SubscriptionStatus,
  SubscriptionTopicSetting,
} from '@ketch-sdk/ketch-types'
import { SetConsentParams } from 'api/externalApis/ketchCdn/fetchers/setConsent'
import { AxiosResponse } from 'axios'
import { MutateOptions } from 'react-query'
import { SetSubscriptionsParams } from 'api/externalApis/ketchCdn/fetchers/setSubscriptions'
import { AllowsOptOutLegalBases, LegalBasis, RequiresOptInLegalBases } from 'utils/constants/legalBases'
import { AuditLogActionVariant } from 'pages/insights/auditLog/components/AuditLogPopup'
import { Pagination } from 'api/common/paginatedQuery'
import { useAuditLogPermits } from 'pages/insights/auditLog/hooks/useAuditLogPermits'
import { useMultiplePermitTransactions } from 'api/auditLogs/queries/useMultiplePermitTransactions'
import { showToast } from 'components/modals/AlertComponent'
import { useAuditLogRightInvocations } from 'pages/insights/auditLog/hooks/useAuditLogRightInvocations'
import { HistoricalPermitPopupVariant } from 'pages/insights/auditLog/components/historicalPermitPopup/constants'

export interface AuditLogContextValues {
  consentData: GetConsentStatusResponseBodyPurposesItemDTO[]
  subscriptionsData: GetConsentStatusResponseBodySubscriptionsItemDTO[][]
  rightsData: GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO[]
  rightsPagination: Pagination
  consentTransactionData: GetPermitTransactionV2ResponseBodyDTO[]
  subscriptionTransactionData: GetPermitTransactionV2ResponseBodyDTO[]
  queries: {
    identifierSearch?: string
    identitySpaceCode: string
    permitDateTime: string
  }
  isLoading: boolean
  isRefetching: boolean
  isFetched: boolean
  isUpdating: boolean
  showNoSearchState: boolean
  showNoDataState: boolean
  auditLogActionVariant: AuditLogActionVariant | null
  auditLogActionPurposeCode: string | null
  auditLogActionSubscriptionCode: string | null
  auditLogActionSubscriptionContactMethods: SubscriptionTopicSetting | null
  setAuditLogActionSubscriptionContactMethods: React.Dispatch<React.SetStateAction<SubscriptionTopicSetting | null>>
  topicSearch: string
  rightIdSearch: string
  setTopicSearch: React.Dispatch<React.SetStateAction<string>>
  setRightIdSearch: React.Dispatch<React.SetStateAction<string>>
  triggerAuditLogPopup: ({
    variant,
    purposeCode,
    subscriptionCode,
  }: {
    variant: AuditLogActionVariant
    purposeCode?: string
    subscriptionCode?: string
  }) => void
  closeAuditLogPopup: () => void
  confirmAuditLogPopup: () => void
  refetch: () => void
  setMultipleQueryParams: (
    newParams: {
      key: 'identitySpaceCode' | 'identifierSearch' | 'permitDateTime'
      value: string | string[] | number | number[] | boolean
    }[],
  ) => void
  setConsent: (
    customMutateAsyncParams?:
      | ({
          params?: Omit<SetConsentParams, never> | undefined
        } & MutateOptions<AxiosResponse<SetConsentResponse, any>, any, SetConsentParams, unknown>)
      | undefined,
  ) => Promise<AxiosResponse<SetConsentResponse, any>>
  setSubscriptions: (
    customMutateAsyncParams?:
      | ({
          params?: Omit<SetSubscriptionsParams, never> | undefined
        } & MutateOptions<AxiosResponse<{}, any>, any, SetSubscriptionsParams, unknown>)
      | undefined,
  ) => Promise<AxiosResponse<{}, any>>
  executeConsentBulkAction: (variant: PermitActionVariant) => Promise<void>
  showViewExperienceModal: boolean
  setShowViewExperienceModal: React.Dispatch<React.SetStateAction<boolean>>
  auditLogHistoricalPermitPopupVariant: HistoricalPermitPopupVariant | null
  setAuditLogHistoricalPermitPopupVariant: React.Dispatch<React.SetStateAction<HistoricalPermitPopupVariant | null>>
  auditLogHistoricalPermitSubscriptionCode: string | null
  setAuditLogHistoricalPermitSubscriptionCode: React.Dispatch<React.SetStateAction<string | null>>
  auditLogExperiencePreviewPermit: GetConsentStatusResponseBodyPurposesItemDTO | PermitHistoryDTO | null
  setAuditLogExperiencePreviewPermit: React.Dispatch<
    React.SetStateAction<GetConsentStatusResponseBodyPurposesItemDTO | PermitHistoryDTO | null>
  >
}

const { Provider, useCustomContext: useAuditLogContext } = createCustomContext<AuditLogContextValues>({
  displayName: 'AuditLogContext',
})

export enum PermitActionVariant {
  Accept = 'acceptAll',
  Reject = 'rejectAll',
  LegalBasisDefault = 'setDefault',
}

export const AuditLogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Utilities
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { queries, setMultipleQueryParams } = useQueryParams<{
    identifierSearch?: string
    identitySpaceCode: string
    permitDateTime: string
  }>()

  // States
  const [auditLogActionVariant, setAuditLogActionVariant] = useState<AuditLogActionVariant | null>(null)
  const [auditLogActionPurposeCode, setAuditLogActionPurposeCode] = useState<string | null>(null)
  const [auditLogActionSubscriptionCode, setAuditLogActionSubscriptionCode] = useState<string | null>(null)
  const [auditLogActionSubscriptionContactMethods, setAuditLogActionSubscriptionContactMethods] =
    useState<SubscriptionTopicSetting | null>(null)
  const [topicSearch, setTopicSearch] = useState('')
  const [rightIdSearch, setRightIdSearch] = useState('')
  const [hasFetched, setHasFetched] = useState(false)
  const [showViewExperienceModal, setShowViewExperienceModal] = useState(false)
  const [auditLogHistoricalPermitPopupVariant, setAuditLogHistoricalPermitPopupVariant] =
    useState<HistoricalPermitPopupVariant | null>(null)
  const [auditLogHistoricalPermitSubscriptionCode, setAuditLogHistoricalPermitSubscriptionCode] = useState<
    string | null
  >(null)
  const [auditLogExperiencePreviewPermit, setAuditLogExperiencePreviewPermit] = useState<
    GetConsentStatusResponseBodyPurposesItemDTO | PermitHistoryDTO | null
  >(null)

  // Queries
  const { organization } = useCurrentOrganization({})
  const {
    data: allPermitData,
    isLoading: isConsentLoading,
    isFetched: isConsentFetched,
    refetch: refetchPermits,
    isRefetching: isConsentRefetching,
  } = useAuditLogPermits()
  const {
    data: allRightsData,
    pagination: rightsPagination,
    isLoading: isRightsLoading,
    isFetched: isRightsFetched,
    isRefetching: isRightsRefetching,
    refetch: refetchRights,
  } = useAuditLogRightInvocations(rightIdSearch ? rightIdSearch : undefined)

  // Filters fetched consent data to only include the latest transaction
  const consentData = useMemo(() => {
    if (!allPermitData.purposes?.length) return []

    const purposes = allPermitData.purposes

    if (!queries.identifierSearch) return []

    // Find the latest timestamp
    let latestTimestamp = -Infinity
    let jurisdictionCode: string | undefined
    purposes.forEach(purpose => {
      if (purpose.collectedAt != null && purpose.collectedAt > latestTimestamp) {
        latestTimestamp = purpose.collectedAt
        jurisdictionCode = purpose.jurisdictionCode
      }
    })

    // Order purposes by name if it exists, otherwise by code
    purposes.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name)
      }
      return a.code?.localeCompare(b.code || '') || 0
    })

    // If no valid timestamps were found
    if (latestTimestamp === -Infinity || !jurisdictionCode) {
      return purposes.filter(purpose => purpose.transactionID)
    }

    // Filter for items with both the latest timestamp and a transactionID
    return purposes.filter(purpose => purpose.transactionID && purpose.jurisdictionCode === jurisdictionCode)
  }, [allPermitData, queries.identifierSearch])

  const rightsData = useMemo(() => {
    if (!allRightsData.length) return []

    if (!queries.identifierSearch) return []

    return allRightsData
  }, [allRightsData, queries.identifierSearch])

  // Filters fetched consent data to only include the latest transaction
  const subscriptionsData = useMemo(() => {
    if (!allPermitData.subscriptions?.length) return []

    const subscriptions = allPermitData.subscriptions

    if (!queries.identifierSearch) return []

    // Order subscriptions by name if it exists, otherwise by code
    subscriptions.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name)
      }
      return a.code?.localeCompare(b.code || '') || 0
    })

    // Group subscriptions by code
    const groupedByCode = subscriptions.reduce(
      (acc, subscription) => {
        const { code, name } = subscription

        // Only include subscriptions with having a name, code, transactionID, and contactMethod
        if (!name || !code || !subscription.transactionID || !subscription.contactMethod) return acc

        if (!acc[code]) {
          acc[code] = []
        }
        acc[code].push(subscription)
        return acc
      },
      {} as Record<string, GetConsentStatusResponseBodySubscriptionsItemDTO[]>,
    )

    // Return array of arrays (grouped subscriptions)
    return Object.values(groupedByCode)
  }, [allPermitData, queries.identifierSearch])

  const consentTransactionIds = useMemo(() => {
    const uniqueIds = new Set<string>()
    consentData.forEach(item => {
      if (item.transactionID) {
        uniqueIds.add(item.transactionID)
      }
    })
    return Array.from(uniqueIds)
  }, [consentData])

  const subscriptionTransactionIds = useMemo(() => {
    const uniqueIds = new Set<string>()
    subscriptionsData.flat().forEach(item => {
      if (item.transactionID) {
        uniqueIds.add(item.transactionID)
      }
    })
    return Array.from(uniqueIds)
  }, [subscriptionsData])

  const {
    data: consentTransactionData,
    isLoading: isConsentTransactionsLoading,
    isFetched: isConsentTransactionsFetched,
    isRefetching: isConsentTransactionsRefetching,
    refetch: refetchConsentTransactions,
  } = useMultiplePermitTransactions(consentTransactionIds)

  const {
    data: subscriptionTransactionData,
    isLoading: isSubscriptionTransactionsLoading,
    isFetched: isSubscriptionTransactionsFetched,
    isRefetching: isSubscriptionTransactionsRefetching,
    refetch: refetchSubscriptionTransactions,
  } = useMultiplePermitTransactions(subscriptionTransactionIds)

  // Mutations
  const { mutateAsync: setConsent, isLoading: isSettingConsent } = useSetConsent({
    onSuccess: () => {
      showToast({ content: 'Consent Permit(s) Updated', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Failed to update consent', type: 'error' })
    },
  })
  const { mutateAsync: setSubscriptions, isLoading: isSettingSubscriptions } = useSetSubscriptions({
    onSuccess: () => {
      showToast({ content: 'Subscription Permit(s) Updated', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Failed to update subscriptions', type: 'error' })
    },
  })

  // Opt-In/Out all purposes
  const executeConsentBulkAction = useCallback(
    async (variant: PermitActionVariant) => {
      if (!consentData.length) return

      // Assemble request data
      const organizationCode = organization?.code
      const propertyCode = consentData[0]?.propertyCode
      const environmentCode = 'production'
      const jurisdictionCode = consentData[0]?.jurisdictionCode
      const identities = {
        [queries.identitySpaceCode]: queries.identifierSearch || '',
      }

      // Build purposes object
      const purposes: SetConsentRequest['purposes'] = consentData.reduce(
        (acc, curr) => {
          if (!curr.code || !curr.legalBasis || !AllowsOptOutLegalBases.includes(curr.legalBasis as LegalBasis))
            return acc

          let allowed: string
          switch (variant) {
            case PermitActionVariant.Accept:
              allowed = 'true'
              break
            case PermitActionVariant.Reject:
              allowed = 'false'
              break
            case PermitActionVariant.LegalBasisDefault:
              allowed = RequiresOptInLegalBases.includes(curr.legalBasis as LegalBasis) ? 'false' : 'true'
              break
          }

          acc[curr.code] = {
            allowed,
            legalBasisCode: curr.legalBasis,
          }

          return acc
        },
        {} as SetConsentRequest['purposes'],
      )

      // Determine permit source
      let source: ConsentSource
      switch (variant) {
        case PermitActionVariant.Accept:
          source = ConsentSource.AuditLogAcceptAll
          break
        case PermitActionVariant.Reject:
          source = ConsentSource.AuditLogRejectAll
          break
        case PermitActionVariant.LegalBasisDefault:
          source = ConsentSource.AuditLogDefault
          break
      }

      const context: PermitRightContext = {
        source,
      }

      // Ensure we have all request fields
      if (
        !organizationCode ||
        !propertyCode ||
        !environmentCode ||
        !jurisdictionCode ||
        !identities[queries.identitySpaceCode]
      )
        return Promise.reject('Missing request data')

      const formData: SetConsentRequest = {
        organizationCode,
        propertyCode,
        environmentCode,
        jurisdictionCode,
        identities,
        purposes,
        context,
      }

      await setConsent({
        params: {
          organizationCode: organization.code,
          formData,
        },
      })

      await refetchPermits()
    },
    [setConsent, consentData, organization, queries, refetchPermits],
  )

  // Opt-In/Out a single purpose
  const executeConsentSingleAction = useCallback(
    async (variant: PermitActionVariant) => {
      if (!consentData.length) return

      // Assemble request data
      const organizationCode = organization?.code
      const propertyCode = consentData[0]?.propertyCode
      const environmentCode = 'production'
      const jurisdictionCode = consentData[0]?.jurisdictionCode
      const identities = {
        [queries.identitySpaceCode]: queries.identifierSearch || '',
      }

      const purpose = consentData.find(purpose => purpose.code === auditLogActionPurposeCode)

      if (!auditLogActionPurposeCode || !purpose || !purpose.legalBasis) return

      // Determine purpose (allowed) and source values
      let allowed: string
      let source: ConsentSource
      switch (variant) {
        case PermitActionVariant.Accept:
          allowed = 'true'
          source = ConsentSource.AuditLogManual
          break
        case PermitActionVariant.Reject:
          allowed = 'false'
          source = ConsentSource.AuditLogManual
          break
        case PermitActionVariant.LegalBasisDefault:
          allowed = RequiresOptInLegalBases.includes(purpose.legalBasis as LegalBasis) ? 'false' : 'true'
          source = ConsentSource.AuditLogDefault
      }
      const context: PermitRightContext = {
        source,
      }

      const purposes: SetConsentRequest['purposes'] = {
        [auditLogActionPurposeCode]: {
          allowed,
          legalBasisCode: purpose.legalBasis,
        },
      }

      // Ensure we have all request fields
      if (
        !organizationCode ||
        !propertyCode ||
        !environmentCode ||
        !jurisdictionCode ||
        !identities[queries.identitySpaceCode]
      )
        return Promise.reject('Missing request data')

      const formData: SetConsentRequest = {
        organizationCode,
        propertyCode,
        environmentCode,
        jurisdictionCode,
        identities,
        purposes,
        context,
      }

      await setConsent({
        params: {
          organizationCode: organization.code,
          formData,
        },
      })

      await Promise.all([refetchPermits()])
    },
    [setConsent, consentData, organization, queries, auditLogActionPurposeCode, refetchPermits],
  )

  // Opt-In/Out all subscriptions
  const executeSubscriptionSingleAction = useCallback(async () => {
    if (!subscriptionsData.length) return

    // Assemble request data
    const organizationCode = organization?.code
    const propertyCode = subscriptionsData[0][0]?.propertyCode
    const environmentCode = 'production'
    const identities = {
      [queries.identitySpaceCode]: queries.identifierSearch || '',
    }

    if (
      !auditLogActionSubscriptionCode ||
      !auditLogActionSubscriptionContactMethods ||
      !Object.keys(auditLogActionSubscriptionContactMethods).length
    )
      return

    const topic: SetSubscriptionsRequest['topics'] = {
      [auditLogActionSubscriptionCode]: auditLogActionSubscriptionContactMethods,
    }

    const context: PermitRightContext = {
      source: SubscriptionSource.AuditLogManual,
    }

    // Ensure we have all request fields
    if (!organizationCode || !propertyCode || !environmentCode || !identities[queries.identitySpaceCode])
      return Promise.reject('Missing request data')

    const formData: SetSubscriptionsRequest = {
      organizationCode,
      propertyCode,
      environmentCode,
      identities,
      topics: topic,
      context,
    }

    await setSubscriptions({
      params: {
        organizationCode: organization.code,
        formData,
      },
    })

    await refetchPermits()
  }, [
    setSubscriptions,
    subscriptionsData,
    organization,
    queries,
    refetchPermits,
    auditLogActionSubscriptionCode,
    auditLogActionSubscriptionContactMethods,
  ])

  // Subscribe/Unsubscribe to a single subscription
  const executeSubscriptionBulkAction = useCallback(
    async (variant: PermitActionVariant) => {
      if (!subscriptionsData.length) return

      // Assemble request data
      const organizationCode = organization?.code
      const propertyCode = subscriptionsData[0][0]?.propertyCode
      const environmentCode = 'production'
      const identities = {
        [queries.identitySpaceCode]: queries.identifierSearch || '',
      }

      // Build topics object
      const topics: SetSubscriptionsRequest['topics'] = subscriptionsData.reduce(
        (acc, topicChannels) => {
          if (!topicChannels.length) return acc

          // All channels for a particular topic have the same topic name and code
          const { code, legalBasis } = topicChannels[0]

          if (!code) return acc

          // Determine what status to set for all channels
          let status: SubscriptionStatus
          switch (variant) {
            case PermitActionVariant.Accept:
              status = SubscriptionStatus.Granted
              break
            case PermitActionVariant.Reject:
              status = SubscriptionStatus.Denied
              break
            case PermitActionVariant.LegalBasisDefault:
              status = RequiresOptInLegalBases.includes(legalBasis as LegalBasis)
                ? SubscriptionStatus.Denied
                : SubscriptionStatus.Granted
              break
          }

          // Build map of channels to statuses
          const topicChannelStatuses: SubscriptionTopicSetting = topicChannels.reduce((acc, channel) => {
            if (!channel.contactMethod) return acc
            acc[channel.contactMethod] = {
              status,
            }
            return acc
          }, {} as SubscriptionTopicSetting)

          // Add this topic to request
          acc[code] = topicChannelStatuses

          return acc
        },
        {} as {
          [key: string]: SubscriptionTopicSetting
        },
      )

      // Determine permit source
      let source: SubscriptionSource
      switch (variant) {
        case PermitActionVariant.Accept:
          source = SubscriptionSource.AuditLogSubscribeAll
          break
        case PermitActionVariant.Reject:
          source = SubscriptionSource.AuditLogUnsubscribeAll
          break
        case PermitActionVariant.LegalBasisDefault:
          source = SubscriptionSource.AuditLogDefault
          break
      }

      const context: PermitRightContext = {
        source,
      }

      // Ensure we have all request fields
      if (!organizationCode || !propertyCode || !environmentCode || !identities[queries.identitySpaceCode])
        return Promise.reject('Missing request data')

      const formData: SetSubscriptionsRequest = {
        organizationCode,
        propertyCode,
        environmentCode,
        identities,
        topics,
        context,
      }

      await setSubscriptions({
        params: {
          organizationCode: organization.code,
          formData,
        },
      })

      await refetchPermits()
    },
    [setSubscriptions, subscriptionsData, organization, queries, refetchPermits],
  )

  // Flags
  const isUpdating = isSettingConsent || isSettingSubscriptions
  const isLoading =
    isConsentLoading || isRightsLoading || isConsentTransactionsLoading || isSubscriptionTransactionsLoading
  const isFetched =
    isConsentFetched && isRightsFetched && isConsentTransactionsFetched && isSubscriptionTransactionsFetched
  const isRefetching =
    isConsentRefetching || isRightsRefetching || isConsentTransactionsRefetching || isSubscriptionTransactionsRefetching
  const noData = !consentData.length && !subscriptionsData.length && !rightsData.length
  const hasBothQueries = !!queries.identitySpaceCode && !!queries.identifierSearch
  const showNoSearchState = !hasBothQueries || (!isFetched && !isRefetching && !hasFetched)
  const showNoDataState = hasBothQueries && noData

  useEffect(() => {
    if (isFetched && !hasFetched) {
      setHasFetched(true)
    }
  }, [isFetched, hasFetched, setHasFetched])

  // Function to refetch both consent and rights data
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchPermits(),
      refetchRights(),
      refetchConsentTransactions(),
      refetchSubscriptionTransactions(),
    ])

    if (!auditLogTabs.some(tab => tab.path === pathname)) {
      navigate(`${auditLogTabs[0].path}${search}`)
    }
  }, [
    refetchPermits,
    refetchRights,
    refetchConsentTransactions,
    refetchSubscriptionTransactions,
    navigate,
    pathname,
    search,
  ])

  // Function to show the audit log action confirmation popup. Optionally accepts purpose and subscription codes
  // which are used when the action is setting a specific purpose or subscription.
  const triggerAuditLogPopup = useCallback(
    ({
      variant,
      purposeCode,
      subscriptionCode,
    }: {
      variant: AuditLogActionVariant
      purposeCode?: string
      subscriptionCode?: string
    }) => {
      setAuditLogActionVariant(variant)
      if (purposeCode) {
        setAuditLogActionPurposeCode(purposeCode)
      }
      if (subscriptionCode) {
        setAuditLogActionSubscriptionCode(subscriptionCode)
      }
    },
    [],
  )

  const closeAuditLogPopup = useCallback(() => {
    setAuditLogActionVariant(null)
  }, [])

  // Close the audit log popup and execute one of the actions
  const confirmAuditLogPopup = useCallback(async () => {
    switch (auditLogActionVariant) {
      case AuditLogActionVariant.AcceptAllConsent:
        await executeConsentBulkAction(PermitActionVariant.Accept)
        break
      case AuditLogActionVariant.RejectAllConsent:
        await executeConsentBulkAction(PermitActionVariant.Reject)
        break
      case AuditLogActionVariant.SetAllDefaultConsent:
        await executeConsentBulkAction(PermitActionVariant.LegalBasisDefault)
        break
      case AuditLogActionVariant.AcceptSingleConsent:
        await executeConsentSingleAction(PermitActionVariant.Accept)
        break
      case AuditLogActionVariant.RejectSingleConsent:
        await executeConsentSingleAction(PermitActionVariant.Reject)
        break
      case AuditLogActionVariant.SetDefaultSingleConsent:
        await executeConsentSingleAction(PermitActionVariant.LegalBasisDefault)
        break
      case AuditLogActionVariant.SubscribeAllSubscriptions:
        await executeSubscriptionBulkAction(PermitActionVariant.Accept)
        break
      case AuditLogActionVariant.UnsubscribeAllSubscriptions:
        await executeSubscriptionBulkAction(PermitActionVariant.Reject)
        break
      case AuditLogActionVariant.SetAllDefaultSubscriptions:
        await executeSubscriptionBulkAction(PermitActionVariant.LegalBasisDefault)
        break
      case AuditLogActionVariant.ManageSingleSubscription:
        await executeSubscriptionSingleAction()
        break
    }
    setAuditLogActionVariant(null)
  }, [
    auditLogActionVariant,
    executeConsentBulkAction,
    executeConsentSingleAction,
    executeSubscriptionBulkAction,
    executeSubscriptionSingleAction,
  ])

  const value = useMemo(
    () => ({
      consentData,
      subscriptionsData,
      rightsData,
      rightsPagination,
      consentTransactionData: consentTransactionData,
      subscriptionTransactionData,
      queries,
      isLoading,
      isRefetching,
      isFetched,
      isUpdating,
      showNoSearchState,
      showNoDataState,
      auditLogActionVariant,
      auditLogActionPurposeCode,
      auditLogActionSubscriptionCode,
      auditLogActionSubscriptionContactMethods,
      setAuditLogActionSubscriptionContactMethods,
      topicSearch,
      rightIdSearch,
      setTopicSearch,
      setRightIdSearch,
      triggerAuditLogPopup,
      closeAuditLogPopup,
      confirmAuditLogPopup,
      refetch,
      setMultipleQueryParams,
      setConsent,
      setSubscriptions,
      executeConsentBulkAction,
      showViewExperienceModal,
      setShowViewExperienceModal,
      auditLogHistoricalPermitPopupVariant,
      setAuditLogHistoricalPermitPopupVariant,
      auditLogHistoricalPermitSubscriptionCode,
      setAuditLogHistoricalPermitSubscriptionCode,
      auditLogExperiencePreviewPermit,
      setAuditLogExperiencePreviewPermit,
    }),
    [
      consentData,
      subscriptionsData,
      rightsData,
      rightsPagination,
      consentTransactionData,
      subscriptionTransactionData,
      queries,
      isLoading,
      isRefetching,
      isFetched,
      isUpdating,
      showNoSearchState,
      showNoDataState,
      auditLogActionVariant,
      auditLogActionPurposeCode,
      auditLogActionSubscriptionCode,
      auditLogActionSubscriptionContactMethods,
      setAuditLogActionSubscriptionContactMethods,
      topicSearch,
      rightIdSearch,
      setTopicSearch,
      setRightIdSearch,
      triggerAuditLogPopup,
      closeAuditLogPopup,
      confirmAuditLogPopup,
      refetch,
      setMultipleQueryParams,
      setConsent,
      setSubscriptions,
      executeConsentBulkAction,
      showViewExperienceModal,
      setShowViewExperienceModal,
      auditLogHistoricalPermitPopupVariant,
      setAuditLogHistoricalPermitPopupVariant,
      auditLogHistoricalPermitSubscriptionCode,
      setAuditLogHistoricalPermitSubscriptionCode,
      auditLogExperiencePreviewPermit,
      setAuditLogExperiencePreviewPermit,
    ],
  )

  return <Provider value={value}>{children}</Provider>
}

export { useAuditLogContext }
